// Static reference data transcribed from the uploaded "InstaPay ISO 20022 -
// Error / Reason Code Reference" PDF (Appendix A, pages 195-206; message-
// reject usage on page 32 and throughout the ISO reason code section).
// This is pure reference content with no backend/Redis/MySQL source - it
// never changes at runtime, so it's baked into the frontend bundle rather
// than served from an API, unlike every other page in this dashboard.

export const TRANSACTION_STATUS_CODES = [
  {
    code: 'ACTC',
    name: 'Accepted Technical Validation',
    meaning: 'Accepted after technical validation - settled/credited.',
  },
  {
    code: 'ACWP',
    name: 'Accepted Without Posting',
    meaning: 'Accepted but not yet posted to the account.',
  },
  {
    code: 'RCVD',
    name: 'Received',
    meaning: 'Message received; processing not yet complete.',
  },
  {
    code: 'RJCT',
    name: 'Rejected',
    meaning: 'Transaction rejected.',
  },
];

// How this repo's own pipeline maps the 4 codes above onto its internal
// callback-record status field, per the PDF's own note.
export const TRANSACTION_STATUS_INTERNAL_MAPPING = [
  { code: 'ACTC', internal: 'settled' },
  { code: 'RJCT', internal: 'rejected' },
  { code: 'ACWP', internal: 'rejected' },
  { code: 'RCVD', internal: 'pending' },
];

// ISO 20022 reason codes (pacs.002 StsRsnInf/Rsn/Cd), grouped by family
// prefix exactly as laid out in the PDF's Appendix A table.
export const ISO_REASON_CODE_GROUPS = [
  {
    prefix: 'AB',
    title: 'Aborted / Timeout',
    codes: [
      { code: 'AB01', meaning: 'Aborted Clearing Timeout' },
      { code: 'AB02', meaning: 'Aborted Clearing Fatal Error' },
      { code: 'AB03', meaning: 'Aborted Settlement Timeout' },
      { code: 'AB04', meaning: 'Aborted Settlement Fatal Error' },
      { code: 'AB05', meaning: 'Timeout Creditor Agent' },
      { code: 'AB06', meaning: 'Timeout Instructed Agent' },
      { code: 'AB07', meaning: 'Offline Agent' },
      { code: 'AB08', meaning: 'Offline Creditor Agent' },
      { code: 'AB09', meaning: 'Error Creditor Agent' },
      { code: 'AB10', meaning: 'Error Instructed Agent' },
      { code: 'AB11', meaning: 'Timeout Debtor Agent' },
    ],
  },
  {
    prefix: 'AC',
    title: 'Account',
    codes: [
      { code: 'AC01', meaning: 'Incorrect Account Number' },
      { code: 'AC02', meaning: 'Invalid Debtor Account Number' },
      { code: 'AC03', meaning: 'Invalid Creditor Account Number' },
      { code: 'AC04', meaning: 'Closed Account Number' },
      { code: 'AC05', meaning: 'Closed Debtor Account Number' },
      { code: 'AC06', meaning: 'Blocked Account' },
      { code: 'AC07', meaning: 'Closed Creditor Account Number' },
      { code: 'AC08', meaning: 'Invalid Branch Code' },
      { code: 'AC09', meaning: 'Invalid Account Currency' },
      { code: 'AC10', meaning: 'Invalid Debtor Account Currency' },
      { code: 'AC11', meaning: 'Invalid Creditor Account Currency' },
      { code: 'AC12', meaning: 'Invalid Account Type' },
      { code: 'AC13', meaning: 'Invalid Debtor Account Type' },
      { code: 'AC14', meaning: 'Invalid Creditor Account Type' },
      { code: 'AC15', meaning: 'Account Details Changed' },
    ],
  },
  {
    prefix: 'AG',
    title: 'Agent / Transaction',
    codes: [
      { code: 'AG01', meaning: 'Transaction Forbidden' },
      { code: 'AG02', meaning: 'Invalid Bank Operation Code' },
      { code: 'AG03', meaning: 'Transaction Not Supported' },
      { code: 'AG04', meaning: 'Invalid Agent Country' },
      { code: 'AG05', meaning: 'Invalid Debtor Agent Country' },
      { code: 'AG06', meaning: 'Invalid Creditor Agent Country' },
      { code: 'AG07', meaning: 'Unsuccessful Direct Debit' },
      { code: 'AG08', meaning: 'Invalid Access Rights' },
      { code: 'AG09', meaning: 'Payment Not Received' },
      { code: 'AG10', meaning: 'Agent Suspended' },
      { code: 'AG11', meaning: 'Creditor Agent Suspended' },
      { code: 'AG12', meaning: 'Not Allowed Book Transfer' },
      { code: 'AG13', meaning: 'Forbidden Return Payment' },
      { code: 'AGNT', meaning: 'Incorrect Agent' },
    ],
  },
  {
    prefix: 'AM',
    title: 'Amount',
    codes: [
      { code: 'AM01', meaning: 'Zero Amount' },
      { code: 'AM02', meaning: 'Not Allowed Amount' },
      { code: 'AM03', meaning: 'Not Allowed Currency' },
      { code: 'AM04', meaning: 'Insufficient Funds' },
      { code: 'AM05', meaning: 'Duplication' },
      { code: 'AM06', meaning: 'Too Low Amount' },
      { code: 'AM07', meaning: 'Blocked Amount' },
      { code: 'AM09', meaning: 'Wrong Amount' },
      { code: 'AM10', meaning: 'Invalid Control Sum' },
      { code: 'AM11', meaning: 'Invalid Transaction Currency' },
      { code: 'AM12', meaning: 'Invalid Amount' },
      { code: 'AM13', meaning: 'Amount Exceeds Clearing System Limit' },
      { code: 'AM14', meaning: 'Amount Exceeds Agreed Limit' },
      { code: 'AM15', meaning: 'Amount Below Clearing System Minimum' },
      { code: 'AM16', meaning: 'Invalid Group Control Sum' },
      { code: 'AM17', meaning: 'Invalid Payment Info Control Sum' },
      { code: 'AM18', meaning: 'Invalid Number Of Transactions' },
      { code: 'AM19', meaning: 'Invalid Group Number Of Transactions' },
      { code: 'AM20', meaning: 'Invalid Payment Info Number Of Transactions' },
      { code: 'AM21', meaning: 'Limit Exceeded' },
      { code: 'AM22', meaning: 'Zero Amount Not Applied' },
      { code: 'AM23', meaning: 'Amount Exceeds Settlement Limit' },
    ],
  },
  {
    prefix: 'BE',
    title: 'Business / Party Element',
    codes: [
      { code: 'BE01', meaning: 'Inconsistent With End Customer' },
      { code: 'BE04', meaning: 'Missing Creditor Address' },
      { code: 'BE05', meaning: 'Unrecognised Initiating Party' },
      { code: 'BE06', meaning: 'Unknown End Customer' },
      { code: 'BE07', meaning: 'Missing Debtor Address' },
      { code: 'BE08', meaning: 'Missing Debtor Name' },
      { code: 'BE09', meaning: 'Invalid Country' },
      { code: 'BE10', meaning: 'Invalid Debtor Country' },
      { code: 'BE11', meaning: 'Invalid Creditor Country' },
      { code: 'BE12', meaning: 'Invalid Country Of Residence' },
      { code: 'BE13', meaning: 'Invalid Debtor Country Of Residence' },
      { code: 'BE14', meaning: 'Invalid Creditor Country Of Residence' },
      { code: 'BE15', meaning: 'Invalid Identification Code' },
      { code: 'BE16', meaning: 'Invalid Debtor Identification Code' },
      { code: 'BE17', meaning: 'Invalid Creditor Identification Code' },
      { code: 'BE18', meaning: 'Invalid Contact Details' },
      { code: 'BE19', meaning: 'Invalid Charge Bearer Code' },
      { code: 'BE20', meaning: 'Invalid Name Length' },
      { code: 'BE21', meaning: 'Missing Name' },
      { code: 'BE22', meaning: 'Missing Creditor Name' },
    ],
  },
  {
    prefix: 'CE',
    title: 'Check ERI',
    codes: [{ code: 'CERI', meaning: 'Check ERI' }],
  },
  {
    prefix: 'CH',
    title: 'CHIPS / Message Element',
    codes: [
      {
        code: 'CH03',
        meaning: 'Requested Execution Date Or Requested Collection Date Too Far In Future',
      },
      {
        code: 'CH04',
        meaning: 'Requested Execution Date Or Requested Collection Date Too Far In Past',
      },
      { code: 'CH07', meaning: 'Element Is Not To Be Used At Bank C Level' },
      { code: 'CH09', meaning: 'Mandate Changes Not Allowed' },
      { code: 'CH10', meaning: 'Information On Mandate Changes Missing' },
      { code: 'CH11', meaning: 'Creditor Identifier Incorrect' },
      { code: 'CH12', meaning: 'Creditor Identifier Not Unambiguously At Transaction Level' },
      { code: 'CH13', meaning: 'Original Debtor Account Is Not To Be Used' },
      { code: 'CH14', meaning: 'Original Debtor Agent Is Not To Be Used' },
      { code: 'CH15', meaning: 'Element Content Includes More Than 140 Characters' },
      { code: 'CH16', meaning: 'Element Content Formally Incorrect' },
      { code: 'CH17', meaning: 'Element Not Admitted' },
      { code: 'CH19', meaning: 'Values Will Be Set To Next TARGET Day' },
      { code: 'CH20', meaning: 'Decimal Points Not Compatible With Currency' },
      { code: 'CH21', meaning: 'Required Compulsory Element Missing' },
      { code: 'CH22', meaning: 'CORE and B2B Within One Message' },
    ],
  },
  {
    prefix: 'CN',
    title: 'Bank Registration',
    codes: [{ code: 'CNOR', meaning: 'Creditor Bank Is Not Registered' }],
  },
  {
    prefix: 'CU',
    title: 'Currency / Customer',
    codes: [
      { code: 'CURR', meaning: 'Incorrect Currency' },
      { code: 'CUST', meaning: 'Requested By Customer' },
    ],
  },
  {
    prefix: 'DN',
    title: 'Bank Registration',
    codes: [{ code: 'DNOR', meaning: 'Debtor Bank Is Not Registered' }],
  },
  {
    prefix: 'DS',
    title: 'Digital Signature',
    codes: [
      { code: 'DS01', meaning: 'Electronic Signatures Correct' },
      { code: 'DS02', meaning: 'Order Cancelled' },
      { code: 'DS03', meaning: 'Order Not Cancelled' },
      { code: 'DS04', meaning: 'Order Rejected or QR Expired' },
      { code: 'DS05', meaning: 'Order Forwarded For Postprocessing' },
      { code: 'DS06', meaning: 'Transfer Order' },
      { code: 'DS07', meaning: 'Processing OK' },
      { code: 'DS08', meaning: 'Decompression Error' },
      { code: 'DS09', meaning: 'Decryption Error' },
      { code: 'DS0A', meaning: 'Data Sign Requested' },
      { code: 'DS0B', meaning: 'Unknown Data Sign Format' },
      { code: 'DS0C', meaning: 'Signer Certificate Revoked' },
      { code: 'DS0D', meaning: 'Signer Certificate Not Valid' },
      { code: 'DS0E', meaning: 'Incorrect Signer Certificate' },
      { code: 'DS0F', meaning: 'Signer Certification Authority Signer Not Valid' },
      { code: 'DS0G', meaning: 'Not Allowed Payment' },
      { code: 'DS0H', meaning: 'Not Allowed Account' },
      { code: 'DS0K', meaning: 'Not Allowed Number Of Transaction' },
      { code: 'DS10', meaning: 'Signer 1 Certificate Revoked' },
      { code: 'DS11', meaning: 'Signer 1 Certificate Not Valid' },
      { code: 'DS12', meaning: 'Incorrect Signer 1 Certificate' },
      { code: 'DS13', meaning: 'Signer Certification Authority Signer 1 Not Valid' },
      { code: 'DS14', meaning: 'User Does Not Exist' },
      { code: 'DS15', meaning: 'Identical Signature Found' },
      { code: 'DS16', meaning: 'Public Key Version Incorrect' },
      { code: 'DS17', meaning: 'Different Order Data In Signatures' },
      { code: 'DS18', meaning: 'Repeat Order' },
      { code: 'DS19', meaning: 'Electronic Signature Rights Insufficient' },
      { code: 'DS20', meaning: 'Signer 2 Certificate Revoked' },
      { code: 'DS21', meaning: 'Signer 2 Certificate Not Valid' },
      { code: 'DS22', meaning: 'Incorrect Signer 2 Certificate' },
      { code: 'DS23', meaning: 'Signer Certification Authority Signer 2 Not Valid' },
      { code: 'DS24', meaning: 'Waiting Time Expired' },
      { code: 'DS25', meaning: 'Order File Deleted' },
      { code: 'DS26', meaning: 'User Signed Multiple Times' },
      { code: 'DS27', meaning: 'User Not Yet Activated' },
    ],
  },
  {
    prefix: 'DT',
    title: 'Date',
    codes: [
      { code: 'DT01', meaning: 'Invalid Date' },
      { code: 'DT02', meaning: 'Invalid Creation Date' },
      { code: 'DT03', meaning: 'Invalid Non Processing Date' },
      { code: 'DT04', meaning: 'Future Date Not Supported' },
      { code: 'DT05', meaning: 'Invalid Cut Off Date' },
      { code: 'DT06', meaning: 'Execution Date Changed' },
    ],
  },
  {
    prefix: 'DU',
    title: 'Duplicate',
    codes: [
      { code: 'DU01', meaning: 'Duplicate Message ID' },
      { code: 'DU02', meaning: 'Duplicate Payment Information ID' },
      { code: 'DU03', meaning: 'Duplicate Transaction' },
      { code: 'DU04', meaning: 'Duplicate End To End ID' },
      { code: 'DU05', meaning: 'Duplicate Instruction ID' },
      { code: 'DUPL', meaning: 'Duplicate Payment' },
    ],
  },
  {
    prefix: 'ED',
    title: 'Settlement',
    codes: [
      { code: 'ED01', meaning: 'Correspondent Bank Not Possible' },
      { code: 'ED03', meaning: 'Balance Info Request' },
      { code: 'ED05', meaning: 'Settlement Failed' },
      { code: 'ED06', meaning: 'Settlement System Not Available' },
    ],
  },
  {
    prefix: 'ER',
    title: 'ERI',
    codes: [{ code: 'ERIN', meaning: 'ERI Option Not Supported' }],
  },
  {
    prefix: 'FF',
    title: 'File / Format',
    codes: [
      { code: 'FF01', meaning: 'Invalid File Format' },
      { code: 'FF02', meaning: 'Syntax Error' },
      { code: 'FF03', meaning: 'Invalid Payment Type Information' },
      { code: 'FF04', meaning: 'Invalid Service Level Code' },
      { code: 'FF05', meaning: 'Invalid Local Instrument Code' },
      { code: 'FF06', meaning: 'Invalid Category Purpose Code' },
      { code: 'FF07', meaning: 'Invalid Purpose' },
      { code: 'FF08', meaning: 'Invalid End To End Id' },
      { code: 'FF09', meaning: 'Invalid Cheque Number' },
      { code: 'FF10', meaning: 'Bank System Processing Error' },
      { code: 'FF11', meaning: 'Clearing Request Aborted' },
    ],
  },
  {
    prefix: 'ID',
    title: 'File',
    codes: [{ code: 'ID01', meaning: 'Corresponding Original File Still Not Sent' }],
  },
  {
    prefix: 'MD',
    title: 'Mandate',
    codes: [
      { code: 'MD01', meaning: 'No Mandate' },
      { code: 'MD02', meaning: 'Missing Mandatory Information In Mandate' },
      { code: 'MD05', meaning: 'Collection Not Due' },
      { code: 'MD06', meaning: 'Refund Request By End Customer' },
      { code: 'MD07', meaning: 'End Customer Deceased' },
    ],
  },
  {
    prefix: 'MS',
    title: 'Unspecified Reason',
    codes: [
      { code: 'MS02', meaning: 'Not Specified Reason Customer Generated' },
      { code: 'MS03', meaning: 'Not Specified Reason Agent Generated' },
    ],
  },
  {
    prefix: 'NA',
    title: 'Narrative',
    codes: [{ code: 'NARR', meaning: 'Narrative' }],
  },
  {
    prefix: 'NE',
    title: 'ERI',
    codes: [{ code: 'NERI', meaning: 'No ERI' }],
  },
  {
    prefix: 'RC',
    title: 'Bank / Routing Identifier',
    codes: [
      { code: 'RC01', meaning: 'Bank Identifier Incorrect' },
      { code: 'RC02', meaning: 'Invalid Bank Identifier' },
      { code: 'RC03', meaning: 'Invalid Debtor Bank Identifier' },
      { code: 'RC04', meaning: 'Invalid Creditor Bank Identifier' },
      { code: 'RC05', meaning: 'Invalid BIC Identifier' },
      { code: 'RC06', meaning: 'Invalid Debtor BIC Identifier' },
      { code: 'RC07', meaning: 'Invalid Creditor BIC Identifier' },
      { code: 'RC08', meaning: 'Invalid Clearing System Member Identifier' },
      { code: 'RC09', meaning: 'Invalid Debtor Clearing System Member Identifier' },
      { code: 'RC10', meaning: 'Invalid Creditor Clearing System Member Identifier' },
      { code: 'RC11', meaning: 'Invalid Intermediary Agent' },
      { code: 'RC12', meaning: 'Missing Creditor Scheme Id' },
      { code: 'RCON', meaning: 'R Message Conflict' },
    ],
  },
  {
    prefix: 'RE',
    title: 'Receiver Info',
    codes: [{ code: 'RECI', meaning: 'Receiver Customer Information' }],
  },
  {
    prefix: 'RF',
    title: 'Reference',
    codes: [{ code: 'RF01', meaning: 'Not Unique Transaction Reference' }],
  },
  {
    prefix: 'RR',
    title: 'Regulatory / Remittance',
    codes: [
      { code: 'RR01', meaning: 'Missing Debtor Account Or Identification' },
      { code: 'RR02', meaning: 'Missing Debtor Name Or Address' },
      { code: 'RR03', meaning: 'Missing Creditor Name Or Address' },
      { code: 'RR04', meaning: 'Regulatory Reason' },
      { code: 'RR05', meaning: 'Regulatory Information Invalid' },
      { code: 'RR06', meaning: 'Tax Information Invalid' },
      { code: 'RR07', meaning: 'Remittance Information Invalid' },
      { code: 'RR08', meaning: 'Remittance Information Truncated' },
      { code: 'RR09', meaning: 'Invalid Structured Creditor Reference' },
      { code: 'RR10', meaning: 'Invalid Character Set' },
      { code: 'RR11', meaning: 'Invalid Debtor Agent Service ID' },
      { code: 'RR12', meaning: 'Invalid Party ID' },
    ],
  },
  {
    prefix: 'SL',
    title: 'Service Level',
    codes: [
      { code: 'SL01', meaning: 'Specific Service Offered By Debtor Agent' },
      { code: 'SL02', meaning: 'Specific Service Offered By Creditor Agent' },
      { code: 'SL03', meaning: 'Service Of Clearing System' },
      { code: 'SL11', meaning: 'Creditor Not On Whitelist Of Debtor' },
      { code: 'SL12', meaning: 'Creditor On Blacklist Of Debtor' },
      { code: 'SL13', meaning: 'Maximum Number Of Direct Debit Transactions Exceeded' },
      { code: 'SL14', meaning: 'Maximum Direct Debit Transaction Amount Exceeded' },
    ],
  },
  {
    prefix: 'TA',
    title: 'Transmission',
    codes: [{ code: 'TA01', meaning: 'Transmission Aborted' }],
  },
  {
    prefix: 'TD',
    title: 'File Data',
    codes: [
      { code: 'TD01', meaning: 'No Data Available' },
      { code: 'TD02', meaning: 'File Non Readable' },
      { code: 'TD03', meaning: 'Incorrect File Structure' },
    ],
  },
  {
    prefix: 'TK',
    title: 'Token',
    codes: [
      { code: 'TK01', meaning: 'Token Invalid' },
      { code: 'TK02', meaning: 'Sender Token Not Found' },
      { code: 'TK03', meaning: 'Receiver Token Not Found' },
      { code: 'TK09', meaning: 'Token Missing' },
      { code: 'TKCM', meaning: 'Token Counterparty Mismatch' },
      { code: 'TKSG', meaning: 'Token Single Use' },
      { code: 'TKSP', meaning: 'Token Suspended' },
      { code: 'TKVE', meaning: 'Token Value Limit Exceeded' },
      { code: 'TKXP', meaning: 'Token Expired' },
    ],
  },
  {
    prefix: 'TM',
    title: 'Cut-off Time',
    codes: [{ code: 'TM01', meaning: 'Invalid Cut Off Time' }],
  },
  {
    prefix: 'TS',
    title: 'Transmission Status',
    codes: [
      { code: 'TS01', meaning: 'Transmission Successful' },
      { code: 'TS04', meaning: 'Transfer To Sign By Hand' },
    ],
  },
];

// InstaPay proprietary reason codes (pacs.002 StsRsnInf/Rsn/Prtry) - used
// instead of an ISO Cd when there's no matching ISO code for the situation.
export const PROPRIETARY_REASON_CODES = [
  { code: '1100', meaning: 'Unrecognized Switch Code' },
  { code: '9909', meaning: 'IPS Switch system malfunction' },
  { code: '9910', meaning: 'Receiving Bank - Logged Off' },
  { code: '9912', meaning: 'Recipient (ISO20022/Legacy) connection is not available' },
  { code: '9913', meaning: 'Recipient IST connection is not available' },
  { code: '9920', meaning: 'Interbank Fee Code invalid' },
  { code: '9921', meaning: 'Application Criteria is invalid' },
  { code: '9922', meaning: 'Type of Person is invalid' },
  { code: '9923', meaning: 'Rate Debit Concept is not alphanumeric' },
  { code: '9924', meaning: 'Invalid Asset Indicator' },
  { code: '9925', meaning: 'Invalid Month of Payment' },
  { code: '9926', meaning: 'Month/Year of Payment is not numeric' },
  { code: '9934', meaning: 'Sending Participant off line' },
  { code: '9946', meaning: 'Sending Participant fully suspended' },
  { code: '9947', meaning: 'Receiving Participant fully suspended' },
  { code: '9948', meaning: 'Service suspended' },
  { code: '9953', meaning: 'Not used' },
  { code: '9955', meaning: 'OFI/Sender not authorised to send business message' },
  { code: '9956', meaning: 'Instructing Agent Funding Account Suspended' },
  { code: '9957', meaning: 'Instructed Agent Funding Account Suspended' },
  { code: '9964', meaning: 'Invalid Bank Identification' },
  { code: 'NOAT', meaning: 'Message Type not supported' },
];

// Cancellation reason codes (camt.056 CxlRsnInf/Rsn/Cd).
export const CANCELLATION_REASON_CODES = [
  { code: 'AC03', meaning: 'Invalid Creditor Account Number' },
  { code: 'AM09', meaning: 'Wrong Amount' },
  { code: 'CUST', meaning: 'Requested By Customer' },
  { code: 'DS24', meaning: 'Time Out' },
  { code: 'DUPL', meaning: 'Duplicate Payment' },
  { code: 'FRAD', meaning: 'Fraudulent Origin' },
  { code: 'FRTR', meaning: 'Final Response' },
  { code: 'TECH', meaning: 'Technical Problem' },
  { code: 'UPAY', meaning: 'Undue Payment' },
];

// Message reject codes (admi.002 Administration Advice).
export const MESSAGE_REJECT_CODES = [
  {
    code: '650',
    name: 'Structural / field validation error',
    meaning:
      'Returned in an admi.002 Administration Advice when an incoming ISO 20022 message fails structural or field-level validation (e.g. invalid character sub-set, invalid code, wrong currency). The message is ignored.',
  },
  {
    code: '690',
    name: 'Missing / invalid signature',
    meaning:
      'Returned in an admi.002 when a message fails the signature security check (e.g. no signature present).',
  },
];

// Codes the PDF's own notes section flags as commonly seen in real InstaPay
// flows - surfaced as a shortcut/highlight list in the UI.
export const COMMONLY_SEEN_CODES = [
  'AM04',
  'DS24',
  'DS0H',
  'AC13',
  'AC12',
  'BE16',
  '9955',
  '9910',
  '9934',
  '9946',
  '9947',
];

export const REFERENCE_NOTES = [
  'A pacs.002 status reason carries either an ISO Cd (StsRsnInf/Rsn/Cd) or a proprietary Prtry value (StsRsnInf/Rsn/Prtry) - never both at once.',
  'Source: Appendix A, pages 195-206 of the InstaPay ISO 20022 reference; message-reject (admi.002) usage is documented on page 32 and referenced throughout the reason-code section.',
];
